/**
 * Header
 * @module components/Header
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import style from './Header.scss';
import { I18nContext } from 'context/i18n.context';

import Link from 'common/Link/Link';
import MainMenu from 'components/MainMenu';
import TopMenu from 'components/TopMenu';
import SearchField from 'components/SearchField';
import MobileMenu from 'components/MobileMenu';
import Icon from 'components/Icon';


/** Class representing the component. */
class Header extends Component {

	static contextType = I18nContext;

	constructor(props) {
		super(props);

		this.state = {
			isVisibleMenu: false,
			isVisibleSearch: false,
			isVisibleLanguageSelect: false
		};
	}


	/**
	 * Toggle mobile menu visibility
	 * @param {Object} e — the event object
	 * 
	 */
	toggleMobileMenu = (e) => {
		if (e) {
			e.preventDefault();
		}

		/* Hide mobile search if visible */
		if (this.state.isVisibleSearch) {
			this.setState({ isVisibleSearch: !this.state.isVisibleSearch });
		}
		/* Hide mobile language select if visible */
		if (this.state.isVisibleLanguageSelect) {
			this.setState({ isVisibleLanguageSelect: !this.state.isVisibleLanguageSelect });
		}
		this.setState({ isVisibleMenu: !this.state.isVisibleMenu });
	}


	/**
	 * Toggle mobile search visibility
	 * @param {Object} e — the event object 
	 */
	toggleMobileSearch (e) {
		e.preventDefault();

		/* Hide mobile menu if visible */
		if (this.state.isVisibleMenu) {
			this.setState({ isVisibleMenu: !this.state.isVisibleMenu });
		}
		/* Hide mobile language select if visible */
		if (this.state.isVisibleLanguageSelect) {
			this.setState({ isVisibleLanguageSelect: !this.state.isVisibleLanguageSelect });
		}
		this.setState({ isVisibleSearch: !this.state.isVisibleSearch });
	}


	/**
	 * Toggle mobile language select visibility
	 * @param {Object} e — the event object 
	 */
	toggleMobileLanguageSelect (e) {
		e.preventDefault();

		/* Hide mobile menu if visible */
		if (this.state.isVisibleMenu) {
			this.setState({ isVisibleMenu: !this.state.isVisibleMenu });
		}
		/* Hide mobile search if visible */
		if (this.state.isVisibleSearch) {
			this.setState({ isVisibleSearch: !this.state.isVisibleSearch });
		}
		this.setState({ isVisibleLanguageSelect: !this.state.isVisibleLanguageSelect });
	}


	/**
	 * Render mobile language select list
	 * @param 
	 */
	renderMobileLanguageSelect(currentLanguage, languages) {
		return (
			<ul className={style.languageList }>
				{languages.map((item, i) => {
					const linkClass = (item.language === currentLanguage) ? style.languageLinkActive : style.languageLink;

					return (
						<li key={i} className={style.languageItem}>
							<a href={item.href} className={linkClass}>
								{item.title}
							</a>
						</li>
					);
				})}
				<li className={style.translate}>
					<span>Google Translate</span>
					<div id="google-translate-dropdown" className="dropdown dropdown-large dropdown-right">
						<p>Use Google to translate the web site. We take no responsibility for the accuracy of the translation.</p>
						<span id="google_translate_element"></span>
					</div>
				</li>
			</ul>
		);
	}


	/**
	 * Gets title of the current language.
	 * @param {String} currentLanguage
	 * @param {Array} languages
	 */
	getCurrentLanguageTitle(currentLanguage, languages) {
		let title = '';

		languages.forEach(item => {
			if (item.language == currentLanguage) {
				title = item.title;
			}
		});

		return title;
	}


	/**
	 * The render method
	 */
	render () {
		const { mainMenu, topMenu } = this.props;
		const { currentLanguage, languages, t } = this.context;
		const mobileMenuClass = this.state.isVisibleMenu ? style.mobileMenuVisible : style.mobileMenu;
		const mobileSearchClass = this.state.isVisibleSearch ? style.mobileSearchVisible : style.mobileSearch;
		const mobileLanguageSelectClass = this.state.isVisibleLanguageSelect ? style.mobileLanguageSelectVisible : style.mobileLanguageSelect;

		return (
			<header className={style.pageHeader}>
				<div className={style.innerContent}>
					<div className={style.logo}>
						<Link route={currentLanguage === 'en' ? '/' : `/${currentLanguage}`}>
							<a>
								<img src="/static/industriALL-logo2.svg" alt="IndustriALL logotype" />
							</a>
						</Link>
					</div>
					<div className={style.menu}>
						<MainMenu links={mainMenu} />
					</div>
					<div className={style.search}>
						<SearchField id="search-header" />
					</div>
					<div className={style.topMenu}>
						<TopMenu links={topMenu} />
					</div>


					<div className={style.mobileToolsMenu}>
						<a className={style.iconLink} href="#mobile-languages" onClick={(e) => this.toggleMobileLanguageSelect(e)} aria-controls="mobile-languages" aria-expanded={this.state.isVisibleLanguageSelect}>
							<span className={style.mobileLanguageCode}>{currentLanguage}<Icon icon="icon-angle-down" /></span>
							<span className="is-visuallyhidden">{t('mobileLanguageIconTitle')}</span>
						</a>

						<a className={style.iconLink} href="#mobile-search" onClick={(e) => this.toggleMobileSearch(e)} aria-controls="mobile-search" aria-expanded={this.state.isVisibleSearch}>
							<Icon icon="icon-search" />
							<span className="is-visuallyhidden">{t('mobileSearchIconTitle')}</span>
						</a>

						<a className={style.iconLink} href="#mobile-menu" onClick={(e) => this.toggleMobileMenu(e)} aria-controls="mobile-menu" aria-expanded={this.state.isVisibleMenu}>
							<Icon icon="icon-bars" />
							<span className="is-visuallyhidden">{t('mobileMenuIconTitle')}</span>
						</a>
						
					</div>

					<div className={mobileMenuClass} id="mobile-menu" aria-hidden={!this.state.isVisibleMenu}>
						<MobileMenu links={mainMenu} toggleMobileMenu={this.toggleMobileMenu} />
					</div>
					<div className={mobileSearchClass} id="mobile-search" aria-hidden={!this.state.isVisibleSearch}>
						<SearchField id="mobile-searchfield" />
					</div>
					<div className={mobileLanguageSelectClass} id="mobile-languages" aria-hidden={!this.state.isVisibleLanguageSelect}>
						{this.renderMobileLanguageSelect(currentLanguage, languages) }
					</div>

				</div>
			</header>
		);
	}
}


/** Prop types **/
Header.propTypes = {
	mainMenu: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			href: PropTypes.string
		})
	),
	topMenu: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			href: PropTypes.string
		})
	)
};


export default Header;
